import { useState, lazy, Suspense } from "react"
import { Link } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Card, Input, Label, Dialog, DialogTitle, Skeleton } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Plus, Building2, Zap } from "lucide-react"
import { LIMITS, getPlan } from "../lib/plans"

const LandingPage = lazy(() => import("./landing"))

/** Dashboard page. Shows all workspaces the user belongs to with cards, plus a create workspace dialog. Falls back to a landing page for logged-out visitors. */
export default function IndexPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session, isPending: sessionLoading } = authClient.useSession()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => fetcher<{ id: string; name: string; slug: string; description: string | null; role: string; plan: string; ownedCount: number }[]>("/api/workspaces"),
    enabled: !!session,
  })

  const create = useMutation({
    mutationFn: (body: { name: string; description?: string }) => fetcher("/api/workspaces", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] })
      toast("Workspace created", "success")
      setOpen(false)
      setName("")
      setDescription("")
    },
    onError: (err: any) => {
      toast(err.message || "Failed to create workspace", "error")
    },
  })

  if (sessionLoading) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-64 space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
  if (!session) return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-64 space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    }>
      <LandingPage />
    </Suspense>
  )

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    create.mutate({ name, description })
  }

  const firstWs = workspaces?.[0]
  const plan = getPlan(firstWs?.plan)
  const wsLimit = LIMITS[plan].workspaces
  const ownedCount = firstWs?.ownedCount ?? 0
  const atWsLimit = wsLimit !== Infinity && ownedCount >= wsLimit

  return (
    <Shell
      title="Workspaces"
      actions={
        atWsLimit && firstWs ? (
          <Link
            to="/workspaces/$workspaceId/billing"
            params={{ workspaceId: firstWs.id }}
            className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium shadow-sm transition-colors hover:bg-muted"
          >
            <Zap className="mr-1 h-4 w-4 text-primary" /> Upgrade to add more
          </Link>
        ) : (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> New workspace
          </Button>
        )
      }
    >
      <main className="mx-auto max-w-6xl p-6">
        <h2 className="mb-4 text-xl font-semibold tracking-tight">Your workspaces</h2>
        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex h-full min-h-[11rem] flex-col rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
                <Skeleton className="mt-4 h-6 w-3/4" />
                <Skeleton className="mt-2 h-4 w-full" />
                <Skeleton className="mt-2 h-4 w-2/3" />
                <Skeleton className="mt-auto h-4 w-24" />
              </div>
            ))}
          </div>
        ) : workspaces?.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((ws) => (
              <Link key={ws.id} to="/workspaces/$workspaceId" params={{ workspaceId: ws.id }} className="block h-full w-full">
                <Card className="group relative flex h-full min-h-[11rem] w-full min-w-[16rem] cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Building2 className="h-6 w-6" />
                    </div>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                      {ws.role}
                    </span>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-semibold tracking-tight transition-colors group-hover:text-primary line-clamp-1">{ws.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">{ws.description || "No description"}</p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <span className="rounded-md bg-primary/10 px-2 py-1 capitalize text-primary">{ws.plan}</span>
                    <span>workspace</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center shadow-sm">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <Building2 className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">No workspaces yet</h3>
            <p className="mb-4 max-w-sm text-sm text-muted-foreground">Create your first workspace to start organizing projects and boards.</p>
            <Button onClick={() => setOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> New workspace
            </Button>
          </div>
        )}
      </main>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <form onSubmit={handleCreate} className="space-y-4">
          <DialogTitle>Create workspace</DialogTitle>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} type="button">Cancel</Button>
            <Button type="submit" isLoading={create.isPending}>Create</Button>
          </div>
        </form>
      </Dialog>
    </Shell>
  )
}
